/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { searchOptions } from 'uplisting-frontend/utils/search';
import type { RhsRecord } from 'uplisting-frontend/utils/rhs/types';
import {
  getMessagesRouteFor,
  getRepliesNewRouteFor,
  getRepliesReplyRouteFor,
} from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  record: RhsRecord;
  savedReplies: ModelRegistry['saved-reply'][];
}

export interface BookingRepliesSignature {
  Element: null;

  Args: IArgs;
}

export default class UiBookingRepliesComponent extends Component<BookingRepliesSignature> {
  @service router!: Services['router'];

  @tracked inputValue = '';
  @tracked showDeleteReplyModal = false;
  @tracked replyToDelete?: ModelRegistry['saved-reply'];

  @cached
  get existingReplies(): ModelRegistry['saved-reply'][] {
    return this.args.savedReplies.filter((record) => record.id);
  }

  @cached
  get filteredReplies(): ModelRegistry['saved-reply'][] {
    return searchOptions<ModelRegistry['saved-reply']>(
      this.existingReplies,
      this.inputValue,
      ['name', 'content'],
    );
  }

  @cached
  get baseRoute(): string {
    return (this.router.currentRouteName ?? '').startsWith('calendar')
      ? 'calendar'
      : 'monthly';
  }

  @cached
  get messagesRoute(): string {
    return getMessagesRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get repliesNewRoute(): string {
    return getRepliesNewRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get replyRoute(): string {
    return getRepliesReplyRouteFor(this.router.currentRouteName ?? '');
  }

  @action
  handleInputSearch(value: string): void {
    this.inputValue = value;
  }

  @action
  @waitFor
  async handleInsertReply(reply: ModelRegistry['saved-reply']): Promise<void> {
    await this.router.transitionTo(this.messagesRoute, {
      queryParams: { reply: reply.id },
    });
  }

  @action
  handleShowDeleteReplyModel(reply: ModelRegistry['saved-reply']): void {
    this.showDeleteReplyModal = true;
    this.replyToDelete = reply;
  }

  @action
  handleCloseDeleteReplyModel(): void {
    this.showDeleteReplyModal = false;
    this.replyToDelete = undefined;
  }

  @action
  @waitFor
  async handleDeleteReply(): Promise<void> {
    (this.replyToDelete as ModelRegistry['saved-reply']).destroyRecord();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Replies': typeof UiBookingRepliesComponent;
  }
}
